import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake, ListChecks, MessageSquare, Sprout } from "lucide-react";
import Reveal from "../components/Reveal";
import { LogoMark } from "../components/Logo";
import { useLang } from "../i18n/LanguageContext";
import Seo from "../components/Seo";

const IMG = {
  story: "https://images.unsplash.com/photo-1629360021730-3d258452c425?q=80&w=1200&auto=format&fit=crop",
};

const ICONS = {
  message: <MessageSquare className="h-6 w-6" />,
  handshake: <HeartHandshake className="h-6 w-6" />,
  list: <ListChecks className="h-6 w-6" />,
  sprout: <Sprout className="h-6 w-6" />,
};

export default function About() {
  const { lang, t } = useLang();
  const base = `/${lang}`;

  return (
    <>
      <Seo title={t.meta.about.title} description={t.meta.about.description} />

      {/* Hero */}
      <section className="bg-paper-warm pb-14 pt-14 md:pb-20 md:pt-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow flex items-center gap-3">
              <span className="h-px w-8 bg-teal-600" aria-hidden="true" />
              {t.about.hero.eyebrow}
            </p>
            <h1 className="mt-6 max-w-3xl font-display text-4xl leading-[1.08] text-navy-900 sm:text-5xl lg:text-6xl" data-testid="about-title">
              {t.about.hero.title}
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-navy-900/75">{t.about.hero.sub}</p>
          </Reveal>
        </div>
      </section>

      {/* Story */}
      <section className="bg-paper-sand/60 py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="eyebrow">{t.about.story.eyebrow}</p>
              <h2 className="mt-4 font-display text-3xl leading-[1.15] text-navy-900 md:text-4xl">{t.about.story.title}</h2>
              <img src={IMG.story} alt={t.about.story.imgAlt} loading="lazy" className="mt-10 aspect-[4/3] w-full rounded-[2rem] border border-navy-900/10 object-cover" />
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.08}>
              <p className="text-xl leading-relaxed text-navy-900">{t.about.story.paras[0]}</p>
              <p className="mt-6 text-[17px] leading-relaxed text-navy-900/75">{t.about.story.paras[1]}</p>
              <p className="mt-6 text-[17px] leading-relaxed text-navy-900/75">{t.about.story.paras[2]}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-paper-warm py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">{t.about.values.eyebrow}</p>
            <h2 className="mt-4 max-w-xl font-display text-4xl leading-[1.1] text-navy-900 md:text-5xl">{t.about.values.title}</h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {t.about.values.items.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.08} className="h-full">
                <div data-testid={`about-value-${i}`} className="h-full rounded-3xl border border-navy-900/10 bg-paper-sand/50 p-7 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-teal-600/40">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-teal-600/15 bg-teal-50 text-teal-700">
                    {ICONS[item.icon]}
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-semibold text-navy-900">{item.title}</h3>
                  <p className="mt-2.5 text-[15px] leading-relaxed text-navy-900/70">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="bg-paper-warm pb-20 md:pb-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-navy-900 px-8 py-14 text-paper-warm md:px-16 md:py-16">
            <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-teal-600/40 blur-2xl" aria-hidden="true" />
            <div className="grid items-center gap-10 lg:grid-cols-12">
              <div className="lg:col-span-8">
                <Reveal>
                  <p className="eyebrow-light">{t.about.location.eyebrow}</p>
                  <h2 className="mt-4 font-display text-3xl leading-[1.12] text-paper-warm md:text-5xl">{t.about.location.title}</h2>
                  <p className="mt-5 max-w-xl text-[17px] leading-relaxed text-paper-warm/75">{t.about.location.body}</p>
                  <Link to={`${base}/contact`} data-testid="about-location-cta" className="btn-light mt-8">
                    {t.about.location.cta}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Reveal>
              </div>
              <div className="lg:col-span-4">
                <Reveal delay={0.1}>
                  <div className="flex flex-col items-start gap-5 rounded-[1.5rem] border border-paper-warm/10 bg-paper-warm/5 p-7">
                    <LogoMark className="h-16 w-16" />
                    <a href="mailto:info@tonouhmarketing.com" data-testid="about-email-link" className="text-sm font-semibold text-paper-warm/85 underline decoration-gold underline-offset-4 transition-colors duration-200 hover:text-paper-warm">
                      info@tonouhmarketing.com
                    </a>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
